import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ChevronDown, Search } from 'lucide-react';
import { Input } from '../components/ui/input';

const faqData = [
  {
    category: 'Booking & Reservations',
    questions: [
      { q: 'How do I book a visit to The Village at Athol Island?', a: 'You can book directly through our website at thevillageatholisland.com. Select your preferred date and time, choose your experience, and complete checkout to receive your booking confirmation and QR code.' },
      { q: 'Do I need to book in advance, or can I walk up?', a: 'Advance booking is strongly recommended, as space is limited per departure. Walk-ups may be accommodated if availability allows, but we cannot guarantee entry without a reservation.' },
      { q: 'Can I modify or cancel my booking?', a: 'Yes. Cancellations made 72+ hours before your scheduled departure receive a full refund. Cancellations 24–72 hours prior receive a 50% refund. No refund is given for cancellations made less than 24 hours before departure or no-shows. See our Cancellation Policy for full details.' },
      { q: 'Will I receive a confirmation after booking?', a: 'Yes. You will receive an on-screen booking confirmation with a QR code immediately after completing your reservation. Please save or print this for check-in.' },
      { q: 'Is there a group rate or private charter option?', a: 'Yes. For groups of 10 or more or for private charter inquiries, please contact us at hello@thevillageatholisland.com for pricing and availability.' },
      { q: 'Can I book for someone else?', a: 'Yes. You can book on behalf of another person. Just enter their name and contact details during checkout. The booking confirmation and QR code will be sent to the email address provided.' },
    ]
  },
  {
    category: 'Getting to The Village',
    questions: [
      { q: 'Where does the boat depart from?', a: 'The boat departs from a designated dock in Nassau. The exact departure location and directions will be included in your booking confirmation email.' },
      { q: 'How long is the boat ride?', a: 'The ride to Athol Island is approximately 15 minutes from Nassau.' },
      { q: 'What time should I arrive for departure?', a: 'Please arrive at least 30 minutes before your scheduled departure time to allow for check-in and boarding.' },
      { q: 'Is parking available near the dock?', a: 'Parking details and nearby options will be provided in your confirmation. Availability may vary by location.' },
      { q: 'Can I be picked up from my hotel or cruise port?', a: 'Transportation from select hotels and the cruise port may be available. Contact us at hello@thevillageatholisland.com for details and pricing.' },
    ]
  },
  {
    category: 'Arrival & Check-In',
    questions: [
      { q: 'What happens when I arrive at the dock?', a: 'When you arrive, our team will greet you at the check-in area. Present your booking QR code, complete a brief safety orientation, and board the vessel for your ride to the island.' },
      { q: 'Do I need to show ID at check-in?', a: 'A valid government-issued ID is recommended for all guests. Guests under 18 must be accompanied by a parent or legal guardian.' },
      { q: 'What if I arrive late?', a: 'If you miss your scheduled departure, we may not be able to accommodate you on a later boat. Please arrive 30 minutes early to ensure a smooth boarding experience.' },
    ]
  },
  {
    category: 'What to Bring & What to Wear',
    questions: [
      { q: 'What should I bring?', a: 'Sunscreen (reef-safe preferred), sunglasses, a hat, a towel, a change of clothes, and a waterproof phone case. We also recommend bringing cash for optional purchases or gratuities.' },
      { q: 'What should I wear?', a: 'Swimwear with a light cover-up is ideal. Wear comfortable water-friendly footwear — flip-flops or water shoes work well. Avoid high heels or formal shoes.' },
      { q: 'Can I bring my own food or drinks?', a: 'Outside food and beverages are not permitted on the island. A variety of freshly prepared food and drinks are available for purchase on-site.' },
      { q: 'Are there lockers or storage for my belongings?', a: 'Secure storage options may be available on the island. We recommend leaving valuables at your hotel or in your vehicle.' },
    ]
  },
  {
    category: 'On the Island',
    questions: [
      { q: 'What is included in my visit?', a: 'Your base admission includes round-trip boat transportation to Athol Island and access to the island grounds, beach areas, and cultural entertainment. Food, beverages, and optional water activities are available for purchase on-site.' },
      { q: 'How long can I stay on the island?', a: 'You are welcome to stay for the duration of the operating day. Return boats run on a set schedule — departure times will be communicated on arrival.' },
      { q: 'Are there restrooms on the island?', a: 'Yes. Restroom facilities are available on the island.' },
      { q: 'Is there Wi-Fi on the island?', a: 'Wi-Fi may be limited. We encourage guests to unplug and enjoy the island experience.' },
      { q: 'Are there shaded areas?', a: 'Yes. Shaded seating, covered dining areas, and natural tree shade are available throughout the island.' },
    ]
  },
  {
    category: 'Food & Drinks',
    questions: [
      { q: 'What kind of food is available?', a: 'The Village serves authentic Bahamian cuisine — including grilled fish, conch dishes, jerk chicken, peas and rice, fresh salads, and more. Vegetarian options are available.' },
      { q: 'Can you accommodate dietary restrictions or allergies?', a: 'We do our best to accommodate dietary needs. Please note any allergies or restrictions during booking or notify staff on arrival. Due to the nature of our open kitchen, we cannot guarantee a fully allergen-free environment.' },
      { q: 'Is alcohol served?', a: 'Yes. A full bar is available with cocktails, rum punch, beer, and non-alcoholic beverages. Guests must be 18+ to purchase alcohol. Please drink responsibly.' },
      { q: 'Can I pre-order my meal?', a: 'Yes! After booking, you can pre-order your meal from our menu. Orders must be placed at least 1 hour before your departure time. Your food will be freshly prepared and ready when you arrive.' },
    ]
  },
  {
    category: 'Activities & Entertainment',
    questions: [
      { q: 'What activities are available?', a: 'Depending on the day and season, activities may include swimming, snorkeling, beach games, volleyball, live music, Junkanoo performances, and cultural demonstrations. Optional add-on activities such as jet ski rides or guided snorkel tours may be available for an additional fee.' },
      { q: 'Is snorkeling gear provided?', a: 'Snorkeling gear may be available for rental or included with select packages. Details will be confirmed at check-in.' },
      { q: 'Are there activities for children?', a: 'Yes. The Village is family-friendly, with shallow beach areas, games, and cultural activities suitable for children. All children must be supervised by a parent or guardian at all times.' },
      { q: 'Can I take photos and videos on the island?', a: 'Absolutely! We encourage guests to capture their experience. Commercial photography or drone use requires prior written approval — contact us in advance.' },
    ]
  },
  {
    category: 'Weather & Cancellations',
    questions: [
      { q: 'What happens if the weather is bad?', a: 'Safety is our top priority. If conditions are unsafe for boating or island operations, we will notify you in advance and offer a full refund or the option to reschedule at no additional cost.' },
      { q: 'Do you operate in the rain?', a: 'Light rain does not typically affect operations. In the event of severe weather, we will contact you with options.' },
    ]
  },
  {
    category: 'Accessibility & Health',
    questions: [
      { q: 'Is the island wheelchair accessible?', a: 'Athol Island is a natural environment with sandy terrain. Wheelchair access is limited. Please contact us in advance so we can advise on how best to accommodate your needs.' },
      { q: 'Are there any health or age restrictions?', a: 'Guests should be in reasonable health for light physical activity and boat travel. Children under 3 may not be permitted on certain water activities. Guests with heart conditions, pregnancy, or recent surgeries should consult a doctor before booking.' },
      { q: 'Is there a first aid station?', a: 'Basic first aid supplies are available on-site. In the event of a medical emergency, our staff are trained to respond and coordinate transport back to Nassau.' },
    ]
  },
  {
    category: 'Payments & Pricing',
    questions: [
      { q: 'What currency are prices listed in?', a: 'All prices are listed in Bahamian Dollars (BSD), which is pegged 1:1 to the US Dollar. US Dollars are accepted on the island.' },
      { q: 'What payment methods are accepted?', a: 'We accept Visa and Mastercard for online payments. Cash (BSD or USD) is also accepted on the island for food, drinks, and optional activities.' },
      { q: 'Is VAT included in the price?', a: 'VAT may be applied at checkout where applicable, in accordance with Bahamian tax regulations.' },
      { q: 'Do you offer refunds for unused add-ons?', a: 'Add-on activities that are cancelled due to weather or operational reasons will be refunded. Unused add-ons due to guest choice are non-refundable.' },
    ]
  },
];

const FAQPage = () => {
  const [openIndex, setOpenIndex] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState(null);

  const toggle = (key) => setOpenIndex(openIndex === key ? null : key);

  const filteredData = faqData.map(cat => ({
    ...cat,
    questions: cat.questions.filter(q =>
      !searchQuery || q.q.toLowerCase().includes(searchQuery.toLowerCase()) || q.a.toLowerCase().includes(searchQuery.toLowerCase())
    )
  })).filter(cat => cat.questions.length > 0);

  const displayData = activeCategory ? filteredData.filter(c => c.category === activeCategory) : filteredData;

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-[#0a1628] py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <Link to="/" className="text-[#00bcd4] hover:text-[#00acc1] text-sm flex items-center gap-1 mb-4">
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </Link>
          <h1 className="font-serif text-4xl md:text-5xl font-bold text-white" data-testid="faq-title">Frequently Asked Questions</h1>
          <p className="text-white/70 mt-3">Everything you need to know about visiting The Village at Athol Island.</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Search */}
        <div className="relative mb-8">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input
            placeholder="Search questions..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-12 py-6 text-base rounded-xl"
            data-testid="faq-search"
          />
        </div>

        {/* Category Pills */}
        <div className="flex flex-wrap gap-2 mb-10">
          <button
            onClick={() => setActiveCategory(null)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${!activeCategory ? 'bg-[#00bcd4] text-white' : 'bg-muted text-muted-foreground hover:bg-muted/80'}`}
            data-testid="faq-category-all"
          >
            All
          </button>
          {faqData.map((cat) => (
            <button
              key={cat.category}
              onClick={() => setActiveCategory(activeCategory === cat.category ? null : cat.category)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${activeCategory === cat.category ? 'bg-[#00bcd4] text-white' : 'bg-muted text-muted-foreground hover:bg-muted/80'}`}
              data-testid={`faq-category-${cat.category.replace(/\s+/g, '-').toLowerCase()}`}
            >
              {cat.category}
            </button>
          ))}
        </div>

        {/* Accordion Groups */}
        {displayData.map((cat) => (
          <div key={cat.category} className="mb-10">
            <h2 className="font-serif text-xl font-bold text-foreground mb-4 pb-2 border-b border-border" data-testid={`faq-group-${cat.category.replace(/\s+/g, '-').toLowerCase()}`}>
              {cat.category}
            </h2>
            <div className="space-y-2">
              {cat.questions.map((item, idx) => {
                const key = `${cat.category}-${idx}`;
                const isOpen = openIndex === key;
                return (
                  <div key={key} className="border border-border rounded-lg overflow-hidden">
                    <button
                      onClick={() => toggle(key)}
                      className="w-full flex items-center justify-between p-4 text-left hover:bg-muted/30 transition-colors"
                      data-testid={`faq-q-${key}`}
                    >
                      <span className="font-medium text-sm pr-4">{item.q}</span>
                      <ChevronDown className={`w-5 h-5 flex-shrink-0 text-muted-foreground transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {isOpen && (
                      <div className="px-4 pb-4 text-sm text-muted-foreground leading-relaxed" data-testid={`faq-a-${key}`}>
                        {item.a}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        {displayData.length === 0 && (
          <div className="text-center py-12">
            <p className="text-muted-foreground">No questions match your search. Try a different term.</p>
          </div>
        )}

        {/* Contact CTA */}
        <div className="mt-12 p-8 bg-[#faf9f6] rounded-2xl text-center">
          <h3 className="font-serif text-xl font-bold mb-2">Still have questions?</h3>
          <p className="text-muted-foreground mb-4">Our team is here to help you plan your perfect island day.</p>
          <p className="text-sm">
            Email us at <a href="mailto:hello@thevillageatholisland.com" className="text-[#00bcd4] hover:underline font-medium">hello@thevillageatholisland.com</a>
            {' '}or call <a href="tel:+12428256265" className="text-[#00bcd4] hover:underline font-medium">+1 (242) 825-6265</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default FAQPage;
